import { relativeWeekend, weekendLabel, weekendEnd } from '@/lib/planning/weekend'
// src/components/schedule/RescheduleGrid.tsx
//
// The shared 2-column icon grid for picking a relative reschedule target — the
// same look as the SchedulePopover quick-date grid, reused so reschedule UIs are
// consistent. Relative tiles call onPick(when); the "Pick date…" tile reveals a
// native date+time entry and calls onPickDate(date, isAllDay) for a precise slot.

import { useState } from 'react'
import { Sun, Moon, Sunrise, CalendarDays, CalendarRange, Calendar, CalendarClock, Hourglass, CalendarPlus, ChevronLeft } from 'lucide-react'
import type { TriageWhen } from './TriageWhenMenu'
import type { DayLoad } from '@/lib/today/dayLoad'
import { DayLoadBar } from './DayLoadBar'
import { SpecificDatePicker } from './SpecificDatePicker'
import { getBaseDate, getNextWeekend, getWeekendAfterNext, getNextMonday, TIME_PRESETS } from '@/lib/dateHelpers'

const WHENS: { when: TriageWhen; label: string; Icon: typeof Sun }[] = [
  { when: 'today', label: 'Today', Icon: Sun },
  { when: 'tonight', label: 'Tonight', Icon: Moon },
  { when: 'tomorrow', label: 'Tomorrow', Icon: Sunrise },
  { when: 'this-weekend', label: 'This weekend', Icon: CalendarDays },
  { when: 'next-weekend', label: 'Next weekend', Icon: CalendarRange },
  { when: 'next-week', label: 'Next week', Icon: Calendar },
  { when: 'this-month', label: 'This month', Icon: CalendarClock },
  { when: 'someday', label: 'Someday', Icon: Hourglass },
]

// Concrete date each dated `when` resolves to — MUST mirror applyTriageWhen in
// lib/triage/applyWhen.ts so the tile never promises a date the action won't
// honor. Surfacing it kills the "which Saturday is 'next weekend'?" ambiguity at
// the point of choice. Pool whens (this-week/this-month/someday) have no
// specific date and show label only.
const WHEN_DATE: Partial<Record<TriageWhen, () => Date>> = {
  tomorrow: () => getBaseDate(1),
  'this-weekend': getNextWeekend,
  'next-weekend': getWeekendAfterNext,
  'next-week': getNextMonday,
}

/**
 * Both days of the weekend a tile means, in order, or null for anything that
 * is not a weekend.
 *
 * "This weekend" resolving silently to Saturday was Scott's finding on the
 * walkthrough: an event has to land on a specific day, and the picker was
 * choosing which one without asking. Read through `weekendStartFor` /
 * `weekendEnd` — the app's ONE weekend definition, which also counts Sunday
 * as part of the weekend in progress. (There is no per-household weekend
 * setting to honour beyond that; `weekStartsOn` is a different question.)
 */
export function weekendDaysFor(when: TriageWhen): [Date, Date] | null {
  if (when !== 'this-weekend' && when !== 'next-weekend') return null
  const saturday = relativeWeekend(when === 'next-weekend')
  return [saturday, weekendEnd(saturday)]
}

/**
 * The DAY a relative tile means, or null for a pool tile that means no day at
 * all (this-week / this-month / someday).
 *
 * Exported because a caller that acts on `onPick(when)` must land on the same
 * day the tile printed. The event panel does exactly that, and when this was
 * private it had no way to — so its tiles did nothing (Scott, 2026-09-24).
 * Midnight, like every helper here: a caller that must keep a time of day
 * supplies it itself.
 */
export function dateForWhen(when: TriageWhen): Date | null {
  if (when === 'today' || when === 'tonight') return getBaseDate(0)
  return WHEN_DATE[when]?.() ?? null
}

/** Compact tile date, e.g. "Sat Jul 4" — weekday included so weekends read clearly. */
function tileDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

const tileClass =
  'flex items-center gap-2 px-2.5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap ' +
  'text-neutral-700 bg-neutral-50 hover:bg-primary-50 hover:text-primary-700 transition-all duration-150'

interface Props {
  flexibleWeekend?: boolean
  /**
   * Which tiles to offer. Omitted, all of them — every existing caller is
   * unchanged. A caller that can only act on some (an event cannot be moved
   * to "Someday") names the ones it can honour, rather than showing tiles that
   * quietly do nothing.
   */
  whens?: readonly TriageWhen[]
  /**
   * Offer BOTH days of a weekend rather than one tile that means Saturday.
   *
   * For a caller that must land on a specific day — an event ends at a
   * particular time, so there is no "either day" for it to use. A task keeps
   * `flexibleWeekend` below, which is the opposite answer to the same
   * question and stays as it was.
   */
  weekendDays?: boolean
  /** `day` is set only for a weekend tile the reader chose a day on. */
  onPick: (when: TriageWhen, day?: Date) => void
  /** When provided, adds a "Pick date…" tile for a specific date/time. */
  onPickDate?: (date: Date, isAllDay: boolean) => void
  /**
   * How full each dated tile's day already is, keyed by `loadKeyFor(when)`.
   * Omit for a plain grid — every existing caller renders exactly as before.
   */
  loads?: Map<string, DayLoad>
  /** Tapping a tile's bar opens the day peek instead of scheduling. */
  onPeek?: (date: Date, when: TriageWhen) => void
}

/**
 * Key a `when` into the loads map. `tonight` is scoped to the evening band, so
 * it gets its own key rather than colliding with `today`'s full-day load.
 */
const localKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** A specific day's key in the loads map — what a weekend day tile reads. */
export function loadKeyForDay(day: Date): string {
  return localKey(day)
}

export function loadKeyFor(when: TriageWhen): string {
  const dateFn = when === 'today' || when === 'tonight' ? () => getBaseDate(0) : WHEN_DATE[when]
  if (!dateFn) return `pool:${when}`
  const d = dateFn()
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return when === 'tonight' ? `${ymd}|evening` : ymd
}

export function RescheduleGrid({ onPick, onPickDate, loads, onPeek, whens, flexibleWeekend = false, weekendDays = false }: Props) {
  const offered = whens ? WHENS.filter((w) => whens.includes(w.when)) : WHENS
  // A weekend becomes its two days, each an ordinary tile — same look, same
  // load bar, same one tap. Never when the caller can take "either day".
  const tiles: Array<(typeof WHENS)[number] & { day?: Date }> = weekendDays && !flexibleWeekend
    ? offered.flatMap((t) => {
      const days = weekendDaysFor(t.when)
      return days ? days.map((day) => ({ ...t, day })) : [t]
    })
    : offered
  const [picking, setPicking] = useState(false)
  const [pickingToday, setPickingToday] = useState(false)

  if (picking && onPickDate) {
    return <SpecificDatePicker onSubmit={(date, isAllDay) => onPickDate(date, isAllDay)} onBack={() => setPicking(false)} />
  }

  // "Today" fans out to a time step (when the host wired onPickDate) so a task can
  // land at a specific hour today, not only all-day. All-day stays one tap away
  // via the prominent button, preserving the fast path.
  if (pickingToday && onPickDate) {
    const base = getBaseDate(0)
    const at = (hour: number, minute = 0) => {
      const d = getBaseDate(0)
      d.setHours(hour, minute, 0, 0)
      onPickDate(d, false)
    }
    return (
      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPickingToday(false) }}
          className="flex items-center gap-1.5 px-1 pb-1 text-[11px] uppercase tracking-wider font-medium text-neutral-400 hover:text-neutral-600"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="px-1 text-[11px] uppercase tracking-wider text-neutral-400">Today · {tileDate(base)}</div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPickDate(base, true) }}
          className={`${tileClass} w-full justify-center`}
        >
          All day
        </button>
        <div className="max-h-64 overflow-y-auto">
          <div className="grid grid-cols-3 gap-2">
            {TIME_PRESETS.map(({ label, hour }) => (
              <button
                key={hour}
                type="button"
                onClick={(e) => { e.stopPropagation(); at(hour) }}
                className={`${tileClass} justify-center`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <input
          type="time"
          aria-label="Other time today"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const v = e.target.value
            if (!v) return
            const [hh, mm] = v.split(':').map(Number)
            at(hh, mm)
          }}
          className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
        />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {tiles.map(({ when, label, Icon, day }) => {
        const dateFn = WHEN_DATE[when]
        const weekend = flexibleWeekend && (when === 'this-weekend' || when === 'next-weekend')
        const sub = day ? tileDate(day)
          : weekend ? weekendLabel(relativeWeekend(when === 'next-weekend')).replace('Weekend · ', '')
            : dateFn ? tileDate(dateFn()) : null
        // Pool whens (this-week / this-month / someday) have no day to measure,
        // so they carry no bar. `loads` is keyed by the caller.
        const load = weekend ? undefined : loads?.get(day ? loadKeyForDay(day) : loadKeyFor(when))
        return (
          <div key={day ? `${when}-${localKey(day)}` : when} data-tile className="flex flex-col">
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation()
                // Today opens the time step when the host can take a specific
                // time; otherwise it stays an instant all-day pick.
                if (when === 'today' && onPickDate) setPickingToday(true)
                // The day is passed ONLY when the reader chose one, so every
                // existing caller sees the call it always saw.
                else if (day) onPick(when, day)
                else onPick(when)
              }}
              className={tileClass}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="flex flex-col leading-tight min-w-0">
                <span>{label}</span>
                {sub && <span className="text-[11px] font-normal text-neutral-400">{sub}</span>}
                {/* Its own line: on one line with the dates it ran past the
                    tile's edge in the side panel (walkthrough, 2026-09-25). */}
                {weekend && <span className="text-[11px] font-normal text-neutral-400">either day</span>}
              </span>
            </button>
            {load && (
              <div className="px-2.5">
                <DayLoadBar load={load} onPeek={onPeek ? () => onPeek(load.date, when) : undefined} />
              </div>
            )}
          </div>
        )
      })}
      {onPickDate && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPicking(true) }}
          className={`${tileClass} col-span-2`}
        >
          <CalendarPlus className="w-4 h-4 shrink-0" />
          <span>Pick date &amp; time…</span>
        </button>
      )}
    </div>
  )
}
